import { jsPDF } from "jspdf";

const fmt = (n) => `$${(Number(n) || 0).toFixed(2)}`;
// Escape content-derived strings before they go into print HTML (prevents stored XSS).
const esc = (v) => String(v ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
const dt = (iso) => { const d = iso ? new Date(iso) : new Date(); return { date: d.toLocaleDateString("en-CA"), time: d.toLocaleTimeString("en-CA", { hour: "2-digit", minute: "2-digit" }) }; };

// Address block: each line of store_address_block becomes its own centered, word-wrapped line.
const addrLines = (s) => (s.store_address_block || "").split("\n").map((l) => l.trim()).filter(Boolean);
const addrBlockHTML = (s) => addrLines(s).map((l) => `<div class="sub">${esc(l)}</div>`).join("");
const addrBlockPDF = (doc, s, center, L, R) => {
  doc.setFontSize(9); doc.setFont("helvetica", "normal");
  addrLines(s).forEach((ln) => doc.splitTextToSize(ln, R - L).forEach((w) => center(w, 9)));
};

// Total the customer saved: (compare-at − our price) per unit, plus any item & cart discounts.
const totalSavings = (sale) => {
  const compareSaved = (sale.items || []).reduce((a, l) =>
    a + Math.max(0, (Number(l.compare_at_price) || 0) - (Number(l.unit_price) || 0)) * (Number(l.quantity) || 0), 0);
  return +(compareSaved + Number(sale.item_discount_total || 0) + Number(sale.discount_total || 0)).toFixed(2);
};

const itemRows = (sale) => sale.items.map((l) => {
  const meta = [l.sku, l.size && `Sz ${l.size}`, l.colour].filter(Boolean).join(" · ");
  const disc = l.discount ? `<div class="idisc">Item discount −${fmt(l.discount)}</div>` : "";
  return `<tr class="item"><td class="iname">
      <div class="nm">${esc(l.product_name)}</div>
      ${meta ? `<div class="meta">${esc(meta)}</div>` : ""}
      <div class="meta">${Number(l.quantity) || 0} × ${fmt(l.unit_price)}</div>${disc}
    </td><td class="amt">${fmt(l.line_total)}</td></tr>`;
}).join("");

export const receiptHTML = (sale, s = {}) => {
  const { date, time } = dt(sale.created_at);
  const itemDisc = Number(sale.item_discount_total || 0);
  const cartDisc = Number(sale.discount_total || 0);
  const savings = totalSavings(sale);
  const logo = s.receipt_logo ? `<div class="logo"><img src="${s.receipt_logo}" alt="logo"/></div>` : "";
  return `<!doctype html><html><head><meta charset="utf-8"><title>${esc(sale.invoice_number)}</title>
  <style>
    @page { size:80mm auto; margin:0; }
    *{ box-sizing:border-box; -webkit-print-color-adjust:exact; print-color-adjust:exact; }
    html,body{ margin:0; padding:0; }
    body{ width:72mm; padding:5mm 4mm; color:#000; font-family:'Segoe UI',Helvetica,Arial,sans-serif; font-size:13px; line-height:1.45; font-weight:500; }
    .logo{ text-align:center; margin-bottom:6px; }
    .logo img{ max-width:68mm; max-height:33mm; object-fit:contain; }
    .store{ text-align:center; font-size:19px; font-weight:800; letter-spacing:.5px; }
    .web{ text-align:center; font-size:15px; font-weight:800; color:#000; margin-top:3px; }
    .sub{ text-align:center; font-size:12px; color:#111; margin-top:2px; }
    .hr{ border-top:1.5px solid #000; margin:8px 0; }
    .mrow{ display:flex; justify-content:space-between; font-size:12.5px; padding:1px 0; }
    .mrow .b{ font-weight:800; }
    table.items{ width:100%; border-collapse:collapse; }
    .item td{ padding:5px 0; vertical-align:top; border-bottom:1px dotted #9a9a9a; }
    .iname .nm{ font-weight:700; font-size:13px; }
    .iname .meta{ font-size:12px; color:#000; font-weight:600; }
    .iname .idisc{ font-size:12px; font-weight:700; color:#000; }
    .amt{ text-align:right; font-weight:800; font-size:13px; white-space:nowrap; padding-left:8px; }
    .trow{ display:flex; justify-content:space-between; font-size:13px; padding:2px 0; }
    .trow.disc{ font-weight:700; }
    .grand{ display:flex; justify-content:space-between; align-items:center; font-size:18px; font-weight:800; border-top:2px solid #000; border-bottom:2px solid #000; padding:6px 0; margin:7px 0; }
    .savings{ text-align:center; font-weight:800; font-size:13px; border:1.5px dashed #000; border-radius:6px; padding:5px; margin-top:8px; letter-spacing:.5px; }
    .foot{ text-align:center; font-size:12px; margin-top:9px; }
    .foot.policy{ font-size:12.5px; font-weight:700; color:#000; margin-top:4px; white-space:pre-line; }
    .foot.small{ font-size:10.5px; color:#222; margin-top:3px; }
  </style></head><body>
    ${logo}
    <div class="store">${esc((s.store_name || "DEALSWAGON OUTLET").toUpperCase())}</div>
    ${s.store_website ? `<div class="web">${esc(s.store_website)}</div>` : ""}
    ${addrBlockHTML(s)}
    ${s.store_phone ? `<div class="sub">${esc(s.store_phone)}</div>` : ""}
    ${s.store_tax_number ? `<div class="sub">GST/HST #: ${esc(s.store_tax_number)}</div>` : ""}
    <div class="hr"></div>
    <div class="mrow"><span>Invoice</span><span class="b">${esc(sale.invoice_number)}</span></div>
    <div class="mrow"><span>Date</span><span>${esc(date)} ${esc(time)}</span></div>
    <div class="mrow"><span>Cashier</span><span>${esc(sale.cashier_name || "")}</span></div>
    <div class="mrow"><span>Payment</span><span>${esc(sale.payment_method || "")}</span></div>
    <div class="hr"></div>
    <table class="items"><tbody>${itemRows(sale)}</tbody></table>
    <div style="height:6px"></div>
    <div class="trow"><span>Subtotal</span><span>${fmt(sale.subtotal)}</span></div>
    ${itemDisc ? `<div class="trow disc"><span>Item discounts</span><span>−${fmt(itemDisc)}</span></div>` : ""}
    ${cartDisc ? `<div class="trow disc"><span>Cart discount</span><span>−${fmt(cartDisc)}</span></div>` : ""}
    <div class="trow"><span>HST (${esc(sale.hst_rate)}%)</span><span>${fmt(sale.hst_amount)}</span></div>
    <div class="grand"><span>TOTAL</span><span>${fmt(sale.total)} ${esc(s.currency || "CAD")}</span></div>
    ${sale.payment_method === "Cash" ? `<div class="trow"><span>Cash received</span><span>${fmt(sale.cash_received)}</span></div><div class="trow"><span>Change</span><span>${fmt(sale.change_due)}</span></div>` : ""}
    ${savings ? `<div class="savings">YOU SAVED ${fmt(savings)}</div>` : ""}
    <div class="hr"></div>
    <div class="foot">${esc(s.invoice_footer || "Thank you for shopping with DealsWagon Outlet.")}</div>
    <div class="foot policy">${esc(s.return_policy || "All sales are subject to store return/exchange policy.")}</div>
    <div class="foot small">${esc(sale.invoice_number)}</div>
  </body></html>`;
};

// Print any receipt HTML via the installed OS printer driver (the one shown in the Ctrl+P list).
// Uses a hidden iframe that prints only after the receipt has fully rendered — a bare
// popup + immediate print() is what caused blank receipts on thermal drivers.
const printHTMLDoc = (html) => {
  const old = document.getElementById("dw-print-frame");
  if (old) old.remove();
  const iframe = document.createElement("iframe");
  iframe.id = "dw-print-frame";
  iframe.setAttribute("aria-hidden", "true");
  iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;";
  iframe.srcdoc = html;
  iframe.onload = () => {
    try {
      const win = iframe.contentWindow;
      win.focus();
      win.onafterprint = () => setTimeout(() => iframe.remove(), 300);
      win.print();
      setTimeout(() => { if (document.getElementById("dw-print-frame")) iframe.remove(); }, 60000);
    } catch (e) {
      console.error("Receipt print failed", e);
      iframe.remove();
    }
  };
  document.body.appendChild(iframe);
};

export const printReceipt = (sale, s) => printHTMLDoc(receiptHTML(sale, s));

export const downloadReceiptPDF = (sale, s = {}) => {
  const W = 226; // ~80mm at 72pt/inch
  const doc = new jsPDF({ unit: "pt", format: [W, 700] });
  const L = 14, R = W - 14;
  let y = 20;
  const center = (t, size = 11, bold = false) => { doc.setFontSize(size); doc.setFont("helvetica", bold ? "bold" : "normal"); doc.text(String(t), W / 2, y, { align: "center" }); y += size + 3; };
  const row = (l, r, size = 10, bold = false) => { doc.setFontSize(size); doc.setFont("helvetica", bold ? "bold" : "normal"); doc.text(String(l), L, y); doc.text(String(r), R, y, { align: "right" }); y += size + 4; };
  const rule = (dash = true, weight = 0.8) => { doc.setLineWidth(weight); doc.setLineDashPattern(dash ? [2, 2] : [], 0); doc.line(L, y, R, y); y += 9; };

  if (s.receipt_logo) {
    try {
      const props = doc.getImageProperties(s.receipt_logo);
      const type = (props.fileType || "PNG").toUpperCase();
      const w = Math.min(195, props.width);
      const h = (w * props.height) / props.width;
      doc.addImage(s.receipt_logo, type, (W - w) / 2, y, w, h);
      y += h + 10;
    } catch (e) { console.warn("Receipt logo skipped (unreadable image)", e); }
  }
  center((s.store_name || "DEALSWAGON OUTLET").toUpperCase(), 14, true);
  if (s.store_website) center(s.store_website, 12, true);
  addrBlockPDF(doc, s, center, L, R);
  if (s.store_phone) center(s.store_phone, 9);
  if (s.store_tax_number) center(`GST/HST #: ${s.store_tax_number}`, 9);
  y += 2; rule(false, 1.2);
  const d = dt(sale.created_at);
  row("Invoice", sale.invoice_number, 10, true);
  row("Date", `${d.date} ${d.time}`);
  row("Cashier", sale.cashier_name || "");
  row("Payment", sale.payment_method || "");
  rule();
  sale.items.forEach((l) => {
    doc.setFontSize(10); doc.setFont("helvetica", "bold");
    const nameLines = doc.splitTextToSize(l.product_name, R - L - 48);
    doc.text(nameLines, L, y);
    doc.text(fmt(l.line_total), R, y, { align: "right" });
    y += nameLines.length * 12;
    const meta = [l.sku, l.size && `Sz ${l.size}`, l.colour].filter(Boolean).join(" · ");
    if (meta) {
      doc.setFontSize(9); doc.setFont("helvetica", "normal");
      doc.text(meta, L, y);
      y += 11;
    }
    doc.setFontSize(10); doc.setFont("helvetica", "bold");
    doc.text(`${l.quantity} x ${fmt(l.unit_price)}${l.discount ? "  (−" + fmt(l.discount) + ")" : ""}`, L, y);
    y += 13;
  });
  rule();
  row("Subtotal", fmt(sale.subtotal));
  if (sale.item_discount_total) row("Item discounts", "-" + fmt(sale.item_discount_total));
  if (sale.discount_total) row("Cart discount", "-" + fmt(sale.discount_total));
  row(`HST (${sale.hst_rate}%)`, fmt(sale.hst_amount));
  y += 2; rule(false, 1.2);
  row("TOTAL", `${fmt(sale.total)} ${s.currency || "CAD"}`, 13, true);
  rule(false, 1.2);
  if (sale.payment_method === "Cash") { row("Cash received", fmt(sale.cash_received)); row("Change", fmt(sale.change_due)); }
  const savings = totalSavings(sale);
  if (savings) { y += 2; center(`YOU SAVED ${fmt(savings)}`, 11, true); }
  y += 6;
  doc.setFontSize(9); doc.setFont("helvetica", "normal");
  doc.text(doc.splitTextToSize(s.invoice_footer || "Thank you for shopping with DealsWagon Outlet.", R - L), W / 2, y, { align: "center" });
  y += 16;
  doc.setFontSize(10.5); doc.setFont("helvetica", "bold");
  doc.text(doc.splitTextToSize(s.return_policy || "All sales are subject to store return/exchange policy.", R - L), W / 2, y, { align: "center" });
  doc.save(`${sale.invoice_number}.pdf`);
};


// ---- Prepared-order summary (unpaid pay-link): items + totals + e-Transfer + Terms + pay QR ----
const orderRef = (o) => o.invoice_number || String(o.order_id || o.order_number || "").slice(-8).toUpperCase();

export const orderSummaryHTML = (order, s = {}) => {
  const itemDisc = Number(order.item_discount_total || 0);
  const cartDisc = Number(order.discount_total || 0);
  const savings = totalSavings(order);
  const logo = s.receipt_logo ? `<div class="logo"><img src="${s.receipt_logo}" alt="logo"/></div>` : "";
  const ref = orderRef(order);
  const terms = s.return_policy || "All sales are subject to store return/exchange policy.";
  return `<!doctype html><html><head><meta charset="utf-8"><title>Order ${esc(ref)}</title>
  <style>
    @page { size:80mm auto; margin:0; }
    *{ box-sizing:border-box; -webkit-print-color-adjust:exact; print-color-adjust:exact; }
    html,body{ margin:0; padding:0; }
    body{ width:72mm; padding:5mm 4mm; color:#000; font-family:'Segoe UI',Helvetica,Arial,sans-serif; font-size:13px; line-height:1.45; font-weight:500; }
    .logo{ text-align:center; margin-bottom:6px; }
    .logo img{ max-width:68mm; max-height:33mm; object-fit:contain; }
    .store{ text-align:center; font-size:19px; font-weight:800; letter-spacing:.5px; }
    .web{ text-align:center; font-size:15px; font-weight:800; color:#000; margin-top:3px; }
    .sub{ text-align:center; font-size:12px; color:#111; margin-top:2px; }
    .label{ text-align:center; font-size:14px; font-weight:800; letter-spacing:1px; margin:4px 0; }
    .hr{ border-top:1.5px solid #000; margin:8px 0; }
    .mrow{ display:flex; justify-content:space-between; font-size:12.5px; padding:1px 0; }
    .mrow .b{ font-weight:800; }
    table.items{ width:100%; border-collapse:collapse; }
    .item td{ padding:5px 0; vertical-align:top; border-bottom:1px dotted #9a9a9a; }
    .iname .nm{ font-weight:700; font-size:13px; }
    .iname .meta{ font-size:12px; color:#000; font-weight:600; }
    .iname .idisc{ font-size:12px; font-weight:700; color:#000; }
    .amt{ text-align:right; font-weight:800; font-size:13px; white-space:nowrap; padding-left:8px; }
    .trow{ display:flex; justify-content:space-between; font-size:13px; padding:2px 0; }
    .trow.disc{ font-weight:700; }
    .grand{ display:flex; justify-content:space-between; align-items:center; font-size:18px; font-weight:800; border-top:2px solid #000; border-bottom:2px solid #000; padding:6px 0; margin:7px 0; }
    .savings{ text-align:center; font-weight:800; font-size:13px; border:1.5px dashed #000; border-radius:6px; padding:5px; margin-top:8px; letter-spacing:.5px; }
    .qr{ text-align:center; margin:10px 0 4px; }
    .qr img{ width:48mm; height:48mm; }
    .qrlabel{ text-align:center; font-weight:800; font-size:14px; letter-spacing:1px; }
    .payurl{ text-align:center; font-size:10px; color:#222; word-break:break-all; margin-top:3px; }
    .blocklabel{ text-align:center; font-weight:800; font-size:12.5px; margin-top:9px; letter-spacing:.5px; }
    .block{ text-align:center; font-size:12px; margin-top:3px; white-space:pre-line; }
    .foot.policy{ text-align:center; font-size:12.5px; font-weight:700; color:#000; margin-top:6px; white-space:pre-line; }
  </style></head><body>
    ${logo}
    <div class="store">${esc((s.store_name || "DEALSWAGON OUTLET").toUpperCase())}</div>
    ${s.store_website ? `<div class="web">${esc(s.store_website)}</div>` : ""}
    ${addrBlockHTML(s)}
    ${s.store_phone ? `<div class="sub">${esc(s.store_phone)}</div>` : ""}
    ${s.store_tax_number ? `<div class="sub">GST/HST #: ${esc(s.store_tax_number)}</div>` : ""}
    <div class="hr"></div>
    <div class="label">ORDER SUMMARY</div>
    <div class="mrow"><span>Order</span><span class="b">${esc(ref)}</span></div>
    <div class="hr"></div>
    <table class="items"><tbody>${itemRows(order)}</tbody></table>
    <div style="height:6px"></div>
    <div class="trow"><span>Subtotal</span><span>${fmt(order.subtotal)}</span></div>
    ${itemDisc ? `<div class="trow disc"><span>Item discounts</span><span>−${fmt(itemDisc)}</span></div>` : ""}
    ${cartDisc ? `<div class="trow disc"><span>Cart discount</span><span>−${fmt(cartDisc)}</span></div>` : ""}
    <div class="trow"><span>HST (${esc(order.hst_rate)}%)</span><span>${fmt(order.hst_amount)}</span></div>
    <div class="grand"><span>TOTAL</span><span>${fmt(order.total)} ${esc(s.currency || "CAD")}</span></div>
    ${savings ? `<div class="savings">YOU SAVE ${fmt(savings)}</div>` : ""}
    ${order.qr_data_url ? `<div class="qr"><div class="qrlabel">SCAN TO PAY</div><img src="${order.qr_data_url}" alt="Scan to pay"/></div>` : ""}
    ${order.pay_url ? `<div class="payurl">${esc(order.pay_url)}</div>` : ""}
    ${s.store_etransfer ? `<div class="blocklabel">PAY BY E-TRANSFER</div><div class="block">${esc(s.store_etransfer)}</div>` : ""}
    <div class="hr"></div>
    <div class="foot policy">${esc(terms)}</div>
    <div class="sub" style="margin-top:8px">Order ${esc(ref)} · hold expires in 24h</div>
  </body></html>`;
};

export const printOrderSummary = (order, s) => printHTMLDoc(orderSummaryHTML(order, s));

export const downloadOrderSummaryPDF = (order, s = {}) => {
  const W = 226;
  const doc = new jsPDF({ unit: "pt", format: [W, 1000] });
  const L = 14, R = W - 14;
  let y = 20;
  const center = (t, size = 11, bold = false) => { doc.setFontSize(size); doc.setFont("helvetica", bold ? "bold" : "normal"); doc.text(String(t), W / 2, y, { align: "center" }); y += size + 3; };
  const row = (l, r, size = 10, bold = false) => { doc.setFontSize(size); doc.setFont("helvetica", bold ? "bold" : "normal"); doc.text(String(l), L, y); doc.text(String(r), R, y, { align: "right" }); y += size + 4; };
  const rule = (dash = true, weight = 0.8) => { doc.setLineWidth(weight); doc.setLineDashPattern(dash ? [2, 2] : [], 0); doc.line(L, y, R, y); y += 9; };
  if (s.receipt_logo) {
    try {
      const props = doc.getImageProperties(s.receipt_logo);
      const type = (props.fileType || "PNG").toUpperCase();
      const w = Math.min(195, props.width);
      const h = (w * props.height) / props.width;
      doc.addImage(s.receipt_logo, type, (W - w) / 2, y, w, h);
      y += h + 10;
    } catch (e) { console.warn("Order summary logo skipped", e); }
  }
  center((s.store_name || "DEALSWAGON OUTLET").toUpperCase(), 14, true);
  if (s.store_website) center(s.store_website, 12, true);
  addrBlockPDF(doc, s, center, L, R);
  if (s.store_phone) center(s.store_phone, 9);
  if (s.store_tax_number) center(`GST/HST #: ${s.store_tax_number}`, 9);
  y += 2; rule(false, 1.2);
  center("ORDER SUMMARY", 13, true);
  row("Order", orderRef(order), 10, true);
  rule();
  order.items.forEach((l) => {
    doc.setFontSize(10); doc.setFont("helvetica", "bold");
    const nameLines = doc.splitTextToSize(l.product_name, R - L - 48);
    doc.text(nameLines, L, y);
    doc.text(fmt(l.line_total), R, y, { align: "right" });
    y += nameLines.length * 12;
    const meta = [l.sku, l.size && `Sz ${l.size}`, l.colour].filter(Boolean).join(" · ");
    if (meta) { doc.setFontSize(9); doc.setFont("helvetica", "normal"); doc.text(meta, L, y); y += 11; }
    doc.setFontSize(10); doc.setFont("helvetica", "bold");
    doc.text(`${l.quantity} x ${fmt(l.unit_price)}${l.discount ? "  (−" + fmt(l.discount) + ")" : ""}`, L, y);
    y += 13;
  });
  rule();
  row("Subtotal", fmt(order.subtotal));
  if (order.item_discount_total) row("Item discounts", "-" + fmt(order.item_discount_total));
  if (order.discount_total) row("Cart discount", "-" + fmt(order.discount_total));
  row(`HST (${order.hst_rate}%)`, fmt(order.hst_amount));
  y += 2; rule(false, 1.2);
  row("TOTAL", `${fmt(order.total)} ${s.currency || "CAD"}`, 13, true);
  rule(false, 1.2);
  const savings = totalSavings(order);
  if (savings) { y += 2; center(`YOU SAVE ${fmt(savings)}`, 11, true); }
  if (order.qr_data_url) {
    y += 8; center("SCAN TO PAY", 11, true);
    try { const qw = 130; doc.addImage(order.qr_data_url, "PNG", (W - qw) / 2, y, qw, qw); y += qw + 6; }
    catch (e) { console.warn("Order summary QR skipped", e); }
  }
  if (order.pay_url) { doc.setFontSize(8); doc.setFont("helvetica", "normal"); doc.text(doc.splitTextToSize(order.pay_url, R - L), W / 2, y, { align: "center" }); y += 14; }
  if (s.store_etransfer) {
    y += 4; center("PAY BY E-TRANSFER", 10, true);
    doc.setFontSize(9); doc.setFont("helvetica", "normal");
    const et = doc.splitTextToSize(s.store_etransfer, R - L);
    doc.text(et, W / 2, y, { align: "center" }); y += et.length * 11 + 4;
  }
  rule();
  doc.setFontSize(9.5); doc.setFont("helvetica", "bold");
  doc.text(doc.splitTextToSize(s.return_policy || "All sales are subject to store return/exchange policy.", R - L), W / 2, y, { align: "center" });
  doc.save(`ORDER-${orderRef(order)}.pdf`);
};


// ---- Refund / Return receipt (visually distinct: red theme + REFUND badge) ----
const returnRows = (ret) => (ret.items || []).map((l) => `
  <tr class="item"><td class="iname">
    <div class="nm">${esc(l.product_name)}</div>
    ${l.sku ? `<div class="meta">${esc(l.sku)}</div>` : ""}
    <div class="meta">${Number(l.quantity) || 0} returned</div>
  </td><td class="amt">−${fmt(l.amount)}</td></tr>`).join("");

export const returnReceiptHTML = (ret, s = {}) => {
  const { date, time } = dt(ret.created_at);
  const logo = s.receipt_logo ? `<div class="logo"><img src="${s.receipt_logo}" alt="logo"/></div>` : "";
  return `<!doctype html><html><head><meta charset="utf-8"><title>REFUND ${esc(ret.invoice_number)}</title>
  <style>
    @page { size:80mm auto; margin:0; }
    *{ box-sizing:border-box; -webkit-print-color-adjust:exact; print-color-adjust:exact; }
    html,body{ margin:0; padding:0; }
    body{ width:72mm; padding:5mm 4mm; color:#000; font-family:'Segoe UI',Helvetica,Arial,sans-serif; font-size:13px; line-height:1.45; font-weight:500; }
    .logo{ text-align:center; margin-bottom:6px; }
    .logo img{ max-width:68mm; max-height:33mm; object-fit:contain; }
    .store{ text-align:center; font-size:19px; font-weight:800; letter-spacing:.5px; }
    .web{ text-align:center; font-size:15px; font-weight:800; margin-top:3px; }
    .sub{ text-align:center; font-size:12px; color:#111; margin-top:2px; }
    .badge{ text-align:center; font-size:17px; font-weight:800; color:#E11D2E; letter-spacing:2px; border:2px solid #E11D2E; border-radius:6px; padding:5px; margin:8px 0; }
    .hr{ border-top:1.5px solid #000; margin:8px 0; }
    .mrow{ display:flex; justify-content:space-between; font-size:12.5px; padding:1px 0; }
    .mrow .b{ font-weight:800; }
    table.items{ width:100%; border-collapse:collapse; }
    .item td{ padding:5px 0; vertical-align:top; border-bottom:1px dotted #9a9a9a; }
    .iname .nm{ font-weight:700; font-size:13px; }
    .iname .meta{ font-size:12px; color:#000; font-weight:600; }
    .amt{ text-align:right; font-weight:800; font-size:13px; white-space:nowrap; padding-left:8px; color:#E11D2E; }
    .trow{ display:flex; justify-content:space-between; font-size:13px; padding:2px 0; }
    .grand{ display:flex; justify-content:space-between; align-items:center; font-size:18px; font-weight:800; color:#E11D2E; border-top:2px solid #E11D2E; border-bottom:2px solid #E11D2E; padding:6px 0; margin:7px 0; }
    .foot{ text-align:center; font-size:12px; margin-top:9px; }
    .foot.policy{ font-size:12.5px; font-weight:700; color:#000; margin-top:4px; white-space:pre-line; }
    .foot.small{ font-size:10.5px; color:#222; margin-top:3px; }
  </style></head><body>
    ${logo}
    <div class="store">${esc((s.store_name || "DEALSWAGON OUTLET").toUpperCase())}</div>
    ${s.store_website ? `<div class="web">${esc(s.store_website)}</div>` : ""}
    ${addrBlockHTML(s)}
    ${s.store_phone ? `<div class="sub">${esc(s.store_phone)}</div>` : ""}
    ${s.store_tax_number ? `<div class="sub">GST/HST #: ${esc(s.store_tax_number)}</div>` : ""}
    <div class="badge">REFUND SUMMARY</div>
    <div class="mrow"><span>Return ref</span><span class="b">${esc(ret.return_id || "")}</span></div>
    <div class="mrow"><span>Original invoice</span><span class="b">${esc(ret.invoice_number)}</span></div>
    <div class="mrow"><span>Date</span><span>${esc(date)} ${esc(time)}</span></div>
    <div class="mrow"><span>Cashier</span><span>${esc(ret.cashier_name || "")}</span></div>
    <div class="mrow"><span>Refund to</span><span>${esc(ret.refund_method || "")}</span></div>
    ${ret.reason ? `<div class="mrow"><span>Reason</span><span>${esc(ret.reason)}</span></div>` : ""}
    <div class="hr"></div>
    <table class="items"><tbody>${returnRows(ret)}</tbody></table>
    <div style="height:6px"></div>
    <div class="trow"><span>Refund subtotal</span><span>−${fmt(ret.refund_subtotal)}</span></div>
    <div class="trow"><span>HST refunded</span><span>−${fmt(ret.hst_refund)}</span></div>
    <div class="grand"><span>TOTAL REFUND</span><span>−${fmt(ret.refund_amount)} ${esc(s.currency || "CAD")}</span></div>
    <div class="hr"></div>
    <div class="foot policy">${esc(s.return_policy || "All sales are subject to store return/exchange policy.")}</div>
    <div class="foot small">Return ${esc(ret.return_id || "")} · ${esc(ret.invoice_number)}</div>
  </body></html>`;
};

export const printReturn = (ret, s) => printHTMLDoc(returnReceiptHTML(ret, s));

export const downloadReturnPDF = (ret, s = {}) => {
  const W = 226;
  const doc = new jsPDF({ unit: "pt", format: [W, 600] });
  const L = 14, R = W - 14;
  let y = 20;
  const center = (t, size = 11, bold = false, color) => { doc.setFontSize(size); doc.setFont("helvetica", bold ? "bold" : "normal"); if (color) doc.setTextColor(...color); else doc.setTextColor(0, 0, 0); doc.text(String(t), W / 2, y, { align: "center" }); y += size + 3; };
  const row = (l, r, size = 10, bold = false, color) => { doc.setFontSize(size); doc.setFont("helvetica", bold ? "bold" : "normal"); if (color) doc.setTextColor(...color); else doc.setTextColor(0, 0, 0); doc.text(String(l), L, y); doc.text(String(r), R, y, { align: "right" }); doc.setTextColor(0, 0, 0); y += size + 4; };
  const rule = (dash = true, weight = 0.8, color) => { doc.setLineWidth(weight); if (color) doc.setDrawColor(...color); else doc.setDrawColor(0, 0, 0); doc.setLineDashPattern(dash ? [2, 2] : [], 0); doc.line(L, y, R, y); doc.setDrawColor(0, 0, 0); y += 9; };
  const RED = [225, 29, 46];

  if (s.receipt_logo) {
    try {
      const props = doc.getImageProperties(s.receipt_logo);
      const type = (props.fileType || "PNG").toUpperCase();
      const w = Math.min(195, props.width);
      const h = (w * props.height) / props.width;
      doc.addImage(s.receipt_logo, type, (W - w) / 2, y, w, h);
      y += h + 10;
    } catch (e) { console.warn("Refund receipt logo skipped (unreadable image)", e); }
  }
  center((s.store_name || "DEALSWAGON OUTLET").toUpperCase(), 14, true);
  if (s.store_website) center(s.store_website, 12, true);
  addrBlockPDF(doc, s, center, L, R);
  if (s.store_phone) center(s.store_phone, 9);
  if (s.store_tax_number) center(`GST/HST #: ${s.store_tax_number}`, 9);
  y += 4;
  center("REFUND SUMMARY", 14, true, RED);
  y += 2; rule(false, 1.2, RED);
  row("Return ref", ret.return_id || "", 10, true);
  row("Original invoice", ret.invoice_number, 10, true);
  const d = dt(ret.created_at);
  row("Date", `${d.date} ${d.time}`);
  row("Cashier", ret.cashier_name || "");
  row("Refund to", ret.refund_method || "");
  if (ret.reason) row("Reason", ret.reason);
  rule();
  (ret.items || []).forEach((l) => {
    doc.setFontSize(10); doc.setFont("helvetica", "bold"); doc.setTextColor(0, 0, 0);
    const nameLines = doc.splitTextToSize(l.product_name, R - L - 48);
    doc.text(nameLines, L, y);
    doc.setTextColor(...RED);
    doc.text(`−${fmt(l.amount)}`, R, y, { align: "right" });
    doc.setTextColor(0, 0, 0);
    y += nameLines.length * 12;
    doc.setFontSize(9); doc.setFont("helvetica", "normal");
    doc.text(`${l.quantity} returned${l.sku ? "  ·  " + l.sku : ""}`, L, y);
    y += 13;
  });
  rule();
  row("Refund subtotal", `−${fmt(ret.refund_subtotal)}`);
  row("HST refunded", `−${fmt(ret.hst_refund)}`);
  y += 2; rule(false, 1.2, RED);
  row("TOTAL REFUND", `−${fmt(ret.refund_amount)} ${s.currency || "CAD"}`, 13, true, RED);
  rule(false, 1.2, RED);
  y += 6;
  doc.setFontSize(10.5); doc.setFont("helvetica", "bold"); doc.setTextColor(0, 0, 0);
  doc.text(doc.splitTextToSize(s.return_policy || "All sales are subject to store return/exchange policy.", R - L), W / 2, y, { align: "center" });
  doc.save(`REFUND-${ret.invoice_number}.pdf`);
};
